import { useId, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerFooter,
  DrawerTopBar,
  DrawerTopBarActionButton,
  SwipeableDrawer,
} from "@/components/Drawer";
import { PriceRangeSlider } from "@/components/Sliders/PriceRangeSlider";
import type { FlightFilters } from "@/features/flights/types/types";

type PriceRangeValue = NonNullable<FlightFilters["priceRange"]>;

type Props = {
  open: boolean;
  defaultValue?: PriceRangeValue;
  value?: PriceRangeValue;
  onSubmit: (newValue: FlightFilters["priceRange"]) => void;
  onClose: () => void;
};

export const PriceRangeFilterDrawer = (props: Props) => {
  const { open, onClose } = props;

  return (
    <SwipeableDrawer open={open} onClose={onClose}>
      <PriceRangeFilterDrawerContent {...props} />
    </SwipeableDrawer>
  );
};

function PriceRangeFilterDrawerContent({
  defaultValue,
  value,
  onSubmit,
  onClose,
}: Props) {
  const intl = useIntl();

  const priceHeaderId = useId();

  const computedDefaultValue = defaultValue ??
    value ?? [0, Number.MAX_SAFE_INTEGER];

  const [localValue, setLocalValue] = useState<PriceRangeValue>(
    value ?? computedDefaultValue,
  );

  return (
    <>
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Price",
          description: "Filter section label for filtering flights by price",
        })}
        actionElement={
          <DrawerTopBarActionButton
            onClick={() => {
              onSubmit(computedDefaultValue);
              setLocalValue(computedDefaultValue);
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Clear filter",
              description: "Button to remove a single active filter",
            })}
          </DrawerTopBarActionButton>
        }
      />

      <DrawerContent>
        <h3 id={priceHeaderId} className="mb-2 text-lg font-medium text-black">
          {intl.formatMessage({
            defaultMessage: "Price Range (IQD)",
            description:
              "Filter label for the price range slider (values in IQD)",
          })}
        </h3>
        <PriceRangeSlider
          aria-labelledby={priceHeaderId}
          min={computedDefaultValue[0]}
          max={computedDefaultValue[1]}
          value={localValue}
          onChange={setLocalValue}
        />
      </DrawerContent>

      <DrawerFooter>
        <Button
          fullWidth
          onClick={() => onSubmit(localValue)}
          variant="contained"
          size="large"
        >
          {intl.formatMessage({
            defaultMessage: "Apply",
            description: "Button to apply selected filters or changes",
          })}
        </Button>
      </DrawerFooter>
    </>
  );
}
